'use client';

import { Button } from "@/components/ui/button";
import { ChevronLeft, ShoppingCart, Gamepad2, Eye } from "lucide-react";
import Link from "next/link";
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, Autoplay } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import Image from "next/image";

function ProductCard({ product }: { product: any }) {
    
    const hasDiscount = product.discount?.percentage > 0 || product.discount?.fixedAmount > 0;

    const calculatePrice = () => {
        if (hasDiscount && product.discount) {
            if (product.discount.percentage && product.discount.percentage > 0) {
                return product.price * (1 - product.discount.percentage / 100);
            }
            if (product.discount.fixedAmount && product.discount.fixedAmount > 0) {
                return product.price - product.discount.fixedAmount;
            }
        }
        return product.price;
    };

    const finalPrice = product.price;

    return (
        <div className="group relative bg-card backdrop-blur-sm rounded-2xl border border-border/5 overflow-hidden transition-all duration-300 hover:border-primary/20 hover:shadow-lg hover:shadow-primary/5">
            {/* Product Image Container */}
            <Link href={`/shop/category/${product.categoryParent?.slug || 'products'}/${product.categoryChild?.slug || 'products'}/${product.slug}`} className="block aspect-square relative overflow-hidden">
                {/* Gradient Overlay */}
                <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-primary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-10" />
                
                {/* Image */}
                <div className="absolute inset-0">
                    <Image 
                        src={product?.coverImage}
                        fill
                        style={{ objectFit: 'cover' }}
                        className="transition-transform duration-500 group-hover:scale-110"
                        sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                        alt={product?.title} 
                    />
                </div>

                {/* Discount Badge */}
                {/* {hasDiscount && (
                    <div className="absolute top-3 right-3 bg-primary/90 backdrop-blur-sm text-primary-foreground text-xs font-medium px-3 py-1.5 rounded-full shadow-lg shadow-primary/20 z-20">
                        {product.discount?.percentage ? `${product.discount.percentage}% تخفیف` : 'تخفیف ویژه'}
                    </div>
                )} */}

                {/* Quick Add Button */}
                <div className="absolute bottom-3 left-1/2 -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-all duration-300 z-20">
                    <Button size="sm" className="rounded-full bg-primary/90 backdrop-blur-sm hover:bg-primary shadow-lg shadow-primary/20">
                        <Eye className="w-4 h-4 ml-2" />
                        برو به صفحه محصول
                    </Button>
                </div>
            </Link>

            {/* Product Info */}
            <div className="p-4">
                <Link href={`/products/${product.slug}`} className="block">
                    <h3 className="font-medium text-base mb-2 line-clamp-1 transition-colors">
                        {product.title}
                    </h3>
                </Link>

                <div className="flex items-center justify-between">
                    <div className="space-y-1">
                        {/* {hasDiscount && (
                            <p className="text-sm text-muted-foreground line-through">
                                {product.price.toLocaleString()} تومان
                            </p>
                        )} */}
                        <p className="text-lg font-semibold bg-gradient-to-r from-primary to-primary/80 bg-clip-text text-transparent">
                            {finalPrice.toLocaleString()} تومان
                        </p>
                    </div>

                    <Button 
                        size="icon" 
                        variant="ghost" 
                        className="rounded-full hover:bg-primary/10 transition-colors"
                    >
                        <Eye className="w-5 h-5" />
                    </Button>
                </div>
            </div>
        </div>
    );
}

interface FeaturedProductsProps {
    products: any[];
}

export default function FeaturedProducts({ products }: FeaturedProductsProps) {
    return (
        <section className="py-20 bg-background">
            <div className="container mx-auto px-4">
                {/* Section Header */}
                <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mb-12">
                    <div className="space-y-1 text-center sm:text-right">
                        <h2 className="text-2xl font-bold">محصولات پرفروش</h2>
                        <p className="text-sm text-muted-foreground">جدیدترین و پرفروش‌ترین محصولات گیمینگ</p>
                    </div>
                    <Link
                        href="/products"
                        className="inline-flex items-center gap-2 text-primary hover:text-primary-light transition-colors"
                    >
                        مشاهده همه
                        <ChevronLeft className="w-4 h-4" />
                    </Link>
                </div>

                {/* Products Swiper */}
                <Swiper
                    modules={[Navigation, Pagination, Autoplay]}
                    spaceBetween={24}
                    slidesPerView={1}
                    pagination={{ clickable: true }}
                    autoplay={{
                        delay: 3000,
                        disableOnInteraction: false,
                    }}
                    grabCursor={false}
                    loop={true}
                    breakpoints={{
                        640: {
                            slidesPerView: 2,
                        },
                        768: {
                            slidesPerView: 3,
                        },
                        1024: {
                            slidesPerView: 4,
                        },
                    }}
                    className="px-4"
                >
                    {products.map((product) => (
                        <SwiperSlide key={product._id.toString()}>
                            <ProductCard product={product} />
                        </SwiperSlide>
                    ))}
                </Swiper>

                {/* View More Button for Mobile */}
                <div className="mt-8 text-center sm:hidden">
                    <Button variant="outline" className="w-full">
                        مشاهده همه محصولات
                        <ChevronLeft className="w-4 h-4 mr-2" />
                    </Button>
                </div>
            </div>
        </section>
    );
} 